import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DashboardService } from '@/services/dashboard-service';
import { useGetBackendUrl } from './use-get-backend-url';
import type {
  DashboardType,
  UpdateDashboardPreferences,
  UpdateWidgetPreference,
  TimeFilter,
} from '@/types/dashboard';

export const useDashboard = () => {
  const backendUrl = useGetBackendUrl();
  const dashboardService = new DashboardService(backendUrl);
  const queryClient = useQueryClient();

  // Get user preferences
  const useUserPreferences = () => {
    return useQuery({
      queryKey: ['dashboard', 'preferences'],
      queryFn: () => dashboardService.getUserPreferences(),
    });
  };

  // Get dashboard-specific preferences
  const useDashboardPreferences = (dashboardType: DashboardType) => {
    return useQuery({
      queryKey: ['dashboard', 'preferences', dashboardType],
      queryFn: () => dashboardService.getDashboardPreferences(dashboardType),
    });
  };

  // Save preferences
  const useSavePreferences = () => {
    return useMutation({
      mutationFn: (preferences: UpdateDashboardPreferences) =>
        dashboardService.savePreferences(preferences),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['dashboard', 'preferences'] });
      },
    });
  };

  // Update widget preferences
  const useUpdateWidgetPreferences = () => {
    return useMutation({
      mutationFn: ({
        dashboardType,
        widgets,
      }: {
        dashboardType: DashboardType;
        widgets: UpdateWidgetPreference[];
      }) => dashboardService.updateWidgetPreferences(dashboardType, widgets),
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: ['dashboard', 'preferences', variables.dashboardType],
        });
      },
    });
  };

  // Reset to defaults
  const useResetToDefaults = () => {
    return useMutation({
      mutationFn: (dashboardType: DashboardType) =>
        dashboardService.resetToDefaults(dashboardType),
      onSuccess: (_, dashboardType) => {
        queryClient.invalidateQueries({
          queryKey: ['dashboard', 'preferences', dashboardType],
        });
      },
    });
  };

  // Get default widgets
  const useDefaultWidgets = (dashboardType: DashboardType) => {
    return useQuery({
      queryKey: ['dashboard', 'defaults', dashboardType],
      queryFn: () => dashboardService.getDefaultWidgets(dashboardType),
    });
  };

  // Get suggested actions
  const useSuggestedActions = (
    timeFilter: TimeFilter = 'all',
    page: number = 1,
    pageSize: number = 10
  ) => {
    return useQuery({
      queryKey: ['dashboard', 'suggested-actions', timeFilter, page, pageSize],
      queryFn: () => dashboardService.getSuggestedActions(timeFilter, page, pageSize),
    });
  };

  // Get suggested actions count
  const useSuggestedActionsCount = (timeFilter: TimeFilter = 'all') => {
    return useQuery({
      queryKey: ['dashboard', 'suggested-actions-count', timeFilter],
      queryFn: () => dashboardService.getSuggestedActionsCount(timeFilter),
    });
  };

  return {
    useUserPreferences,
    useDashboardPreferences,
    useSavePreferences,
    useUpdateWidgetPreferences,
    useResetToDefaults,
    useDefaultWidgets,
    useSuggestedActions,
    useSuggestedActionsCount,
  };
};
